import { motion } from 'framer-motion'
import type { WeatherInsight } from '@/services/weatherService'

interface WeatherCardProps {
  weather: WeatherInsight
}

export function WeatherCard({ weather }: WeatherCardProps) {
  // Get UV level description
  const getUVDescription = (uv: number): { label: string; color: string } => {
    if (uv >= 8) return { label: 'Very High', color: '#EF4444' }
    if (uv >= 6) return { label: 'High', color: '#F97316' }
    if (uv >= 3) return { label: 'Moderate', color: '#EAB308' }
    return { label: 'Low', color: '#22C55E' }
  }

  // Get humidity description
  const getHumidityDescription = (humidity: number): string => {
    if (humidity >= 70) return 'High'
    if (humidity >= 40) return 'Moderate'
    return 'Low'
  }

  // Get temperature description
  const getTempDescription = (min: number, max: number): string => {
    const avg = (min + max) / 2
    if (avg >= 85) return 'Hot'
    if (avg >= 70) return 'Warm'
    if (avg >= 55) return 'Mild'
    if (avg >= 40) return 'Cool'
    return 'Cold'
  }

  const uvInfo = getUVDescription(weather.uvIndex)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#46C0C6]/5 to-[#46C0C6]/10 rounded-2xl p-5 border border-[#46C0C6]/20"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">📍</span>
        <h3 className="font-semibold text-gray-900">Curated for {weather.location}</h3>
      </div>

      {/* Weather Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Temperature */}
        <div className="bg-white rounded-xl p-3 text-center">
          <span className="text-2xl mb-1 block">🌡️</span>
          <div className="text-sm font-semibold text-gray-900">
            {weather.tempRange.min}°-{weather.tempRange.max}°F
          </div>
          <div className="text-xs text-gray-500">
            {getTempDescription(weather.tempRange.min, weather.tempRange.max)}
          </div>
        </div>

        {/* Humidity */}
        <div className="bg-white rounded-xl p-3 text-center">
          <span className="text-2xl mb-1 block">💧</span>
          <div className="text-sm font-semibold text-gray-900">{weather.avgHumidity}%</div>
          <div className="text-xs text-gray-500">{getHumidityDescription(weather.avgHumidity)}</div>
        </div>

        {/* UV Index */}
        <div className="bg-white rounded-xl p-3 text-center">
          <span className="text-2xl mb-1 block">☀️</span>
          <div className="text-sm font-semibold" style={{ color: uvInfo.color }}>
            UV {weather.uvIndex}
          </div>
          <div className="text-xs text-gray-500">{uvInfo.label}</div>
        </div>
      </div>

      {/* Skin Implications */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700">What this means for your skin:</h4>
        <ul className="space-y-1.5">
          {weather.skinImplications.map((implication, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="text-[#46C0C6] mt-0.5">•</span>
              <span>{implication}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Texture Recommendation Badge */}
      <div className="mt-4 pt-4 border-t border-[#46C0C6]/20">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Recommended textures:</span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#46C0C6] text-white">
            {weather.textureRecommendation === 'lightweight'
              ? '💧 Lightweight'
              : weather.textureRecommendation === 'rich'
                ? '🧴 Rich'
                : '✨ Medium'}
          </span>
        </div>
      </div>
    </motion.div>
  )
}
